import { ReactNode, useLayoutEffect, useRef } from "react";
import { InfoTooltip } from "@/lib/dashboard/InfoTooltip";

/**
 * Le due scale dei numeri delle dashboard, in un posto solo.
 *
 * Non sono taglie fisse ma corse: su uno schermo stretto la cifra sta appena sopra il
 * testo corrente -- di piu' manderebbe a capo "2.642 €" o lo farebbe uscire dalla colonna
 * -- e cresce con lo spazio disponibile, dove invece un numero della misura del testo si
 * perderebbe fra le etichette.
 *
 * `lg` e' il numero che risponde alla domanda della card; `md` sta dove il numero divide
 * la riga con la propria etichetta e non puo' prendersi tutta l'altezza.
 */
export const NUMBER_SCALE = {
  lg: "text-lg sm:text-xl lg:text-2xl xl:text-3xl",
  md: "text-base sm:text-lg lg:text-xl xl:text-2xl",
} as const;

export type NumberScale = keyof typeof NUMBER_SCALE;

/** Sotto questa misura il numero non scende: va a capo piuttosto che diventare illeggibile. */
const MIN_FONT_PX = 12;

interface FitNumberProps {
  value: string;
  size?: NumberScale;
  className?: string;
  /**
   * Cosa disegnare al posto del testo, con la stessa misura e lo stesso adattamento alla
   * colonna. `value` resta la chiave del ricalcolo. Serve ai segnaposto animati: dentro
   * lo stesso contenitore occupano esattamente lo spazio del numero che arrivera'.
   */
  children?: ReactNode;
}

/**
 * Un numero della scala che non viene mai troncato.
 *
 * La scala segue la larghezza dello schermo, ma lo spazio vero e' quello della card: su md
 * la sidebar e le card affiancate lasciano colonne strette proprio mentre il carattere sale,
 * e con `truncate` le cifre finivano in ellissi. Qui si parte dalla misura della scala e, se
 * il numero non ci sta, si scende di un pixel alla volta finche' ci sta; si ricalcola quando
 * cambia la larghezza. Al minimo, se ancora non basta, va a capo: mai tagliato.
 */
export function FitNumber({ value, size = "lg", className = "", children }: FitNumberProps) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let lastWidth = -1;

    const fit = () => {
      if (el.clientWidth === lastWidth) return;
      lastWidth = el.clientWidth;
      // Si riparte dalla misura della scala: allargando la card il numero deve ricrescere.
      el.style.fontSize = "";
      el.style.whiteSpace = "nowrap";
      let px = parseFloat(getComputedStyle(el).fontSize);
      while (el.scrollWidth > el.clientWidth && px > MIN_FONT_PX) {
        px -= 1;
        el.style.fontSize = `${px}px`;
      }
      if (el.scrollWidth > el.clientWidth) el.style.whiteSpace = "normal";
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, size]);

  return (
    <div
      ref={ref}
      className={`${NUMBER_SCALE[size]} font-semibold tabular-nums leading-tight min-w-0 ${className}`}
    >
      {children ?? value}
    </div>
  );
}

interface BigNumberProps {
  value: string;
  label: string;
  info?: string;
  /** Tinta del numero: i soldi si leggono anche dal colore, i conteggi no. */
  valueClassName?: string;
  size?: NumberScale;
  /** Al posto della cifra, nello stesso riquadro: vedi `FitNumber.children`. */
  valueContent?: ReactNode;
}

/**
 * Un numero con la sua didascalia sotto.
 *
 * Le card in cima alla Panoramica e la riga dei Pagamenti ne affiancano da due a cinque a
 * seconda di chi guarda: tenerne il formato qui e' cio' che li fa stare sulla stessa linea
 * di base anche quando arrivano da pagine diverse.
 */
export function BigNumber({
  value,
  label,
  info,
  valueClassName = "text-gray-900",
  size = "lg",
  valueContent,
}: BigNumberProps) {
  return (
    <div className="min-w-0">
      <FitNumber value={value} size={size} className={valueClassName}>
        {valueContent}
      </FitNumber>
      <span className="text-xs text-gray-500">
        {label}
        {info && (
          <>
            {" "}
            <InfoTooltip text={info} />
          </>
        )}
      </span>
    </div>
  );
}

export default BigNumber;
